import fileDownload from 'js-file-download';
import OptimiserStatusApi from 'api/optimiserStatus';
import { filterViolationsForRecord } from 'library/optimiserStatusWarning';
import { OPTIMISER_STATUS as actionType } from './actionTypes';

// At runtime the axios response interceptor wraps response.data as an Immutable
// structure; under axios-mock-adapter (tests) it stays plain JS. Convert to plain
// JS so the reducer and components always work with ordinary objects/arrays.
const toPlain = data => (data && typeof data.toJS === 'function' ? data.toJS() : data);

const summaryInFlight = () => ({
  type: actionType.SUMMARY_IN_FLIGHT
});

const summarySuccess = data => ({
  type: actionType.SUMMARY_SUCCESS,
  data
});

const summaryFailure = error => ({
  type: actionType.SUMMARY_FAILURE,
  error
});

const violationsInFlight = () => ({
  type: actionType.VIOLATIONS_IN_FLIGHT
});

const violationsSuccess = data => ({
  type: actionType.VIOLATIONS_SUCCESS,
  data
});

const violationsFailure = error => ({
  type: actionType.VIOLATIONS_FAILURE,
  error
});

export const setFilters = filters => ({
  type: actionType.SET_FILTERS,
  filters
});

export const loadSummary = () => (dispatch) => {
  dispatch(summaryInFlight());
  return OptimiserStatusApi.getSummary()
    .then((response) => {
      const data = toPlain(response.data);
      dispatch(summarySuccess(data));
      return data;
    })
    .catch((error) => {
      dispatch(summaryFailure(error));
      throw error;
    });
};

export const loadViolations = (props = {}) => (dispatch) => {
  dispatch(violationsInFlight());
  return OptimiserStatusApi.getViolations(props)
    .then((response) => {
      const data = toPlain(response.data);
      dispatch(violationsSuccess(data));
      return data;
    })
    .catch((error) => {
      dispatch(violationsFailure(error));
      throw error;
    });
};

// Fetch the (filtered) failure list as CSV and hand it to the browser as a
// download. No reducer state is needed for this; it is a pure side effect.
export const downloadCsv = (props = {}) => () => OptimiserStatusApi.getExport(props)
  .then((response) => {
    fileDownload(response.data, 'optimiser-status.csv');
    return response;
  });

// Fetch violations for a specific record directly, without dispatching into the
// shared violations redux slice (which would clobber the Status page table).
// Returns the filtered array on success; throws on API failure.
export const loadViolationsForRecord = ({ recordId, recordType = 'user' }) => () => OptimiserStatusApi.getViolations({ recordType, pageSize: 200 })
  .then((response) => {
    const data = toPlain(response.data) || {};
    return filterViolationsForRecord(data.results || [], recordId, recordType);
  });

const attemptsInFlight = () => ({ type: actionType.ATTEMPTS_IN_FLIGHT });
const attemptsSuccess = data => ({ type: actionType.ATTEMPTS_SUCCESS, data });
const attemptsFailure = error => ({ type: actionType.ATTEMPTS_FAILURE, error });

export const loadPublishAttempts = () => (dispatch) => {
  dispatch(attemptsInFlight());
  return OptimiserStatusApi.getPublishAttempts()
    .then((response) => {
      const data = toPlain(response.data);
      dispatch(attemptsSuccess(data));
      return data;
    })
    .catch((error) => {
      dispatch(attemptsFailure(error));
      throw error;
    });
};

export const retryPublish = id => () => OptimiserStatusApi.retryPublish(id);

// Load one page of a publish attempt's records for the side panel. Like
// loadViolationsForRecord, it returns the data and leaves the store alone.
export const loadAttemptRecords = (id, params) => () => OptimiserStatusApi.getAttemptRecords(id, params)
  .then(response => toPlain(response.data));

const writebackInFlight = () => ({ type: actionType.WRITEBACK_IN_FLIGHT });
const writebackSuccess = data => ({ type: actionType.WRITEBACK_SUCCESS, data });
const writebackFailure = error => ({ type: actionType.WRITEBACK_FAILURE, error });

export const loadWritebackHistory = () => (dispatch) => {
  dispatch(writebackInFlight());
  return OptimiserStatusApi.getWritebackHistory()
    .then((response) => {
      const data = toPlain(response.data);
      dispatch(writebackSuccess(data));
      return data;
    })
    .catch((error) => {
      dispatch(writebackFailure(error));
      throw error;
    });
};
